import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  input,
  Input,
  Output,
} from "@angular/core";
import { FormGroup } from "@angular/forms";

import { AddConnectionDialogComponent, AddLocationDialogComponent } from "@components/index";
import { PurchaseOrderStatus } from "@components/purchase-orders/models/purchase-order-status.enum";
import { IPurchaseOrderExtended } from "@components/purchase-orders/models/purchase-order.interface";
import { SlideOverlayPageClass } from "@components/shared/overlay/slide-overlay-page/slide-overlay-page.class";
import { TextConstants } from "@shared/constants";
import { ILocationExtended, ISelectOption } from "@shared/interfaces";
import { NavigationParams, RouterService } from "@shared/services/router.service";
import { CommonUtils, LocalizationUtils } from "@shared/utils";

@Component({
  selector: "app-purchase-order-main-information",
  templateUrl: "./purchase-order-main-information.component.html",
  styleUrl: "./purchase-order-main-information.component.scss",
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class PurchaseOrderMainInformationComponent extends SlideOverlayPageClass {
  private router = inject(RouterService);

  countryOptions = input<ISelectOption[]>([]);

  @Input()
  formGroup: FormGroup;

  organisationsOptions = input<ISelectOption[]>([]);

  activeOrganisationOption = input<ISelectOption>(null);

  locationsOptions = input<ISelectOption[]>([]);

  override element = input<IPurchaseOrderExtended>(null);

  @Output()
  updateSupplierOrganisation: EventEmitter<string> = new EventEmitter<string>();

  @Output()
  updateBuyerOrganisation: EventEmitter<string> = new EventEmitter<string>();

  @Output()
  updateDeliverToLocation: EventEmitter<string> = new EventEmitter<string>();

  statusOtions: ISelectOption[] = [
    {
      value: PurchaseOrderStatus.APPROVED,
      label: LocalizationUtils.getStatus(PurchaseOrderStatus.APPROVED),
    },
    {
      value: PurchaseOrderStatus.OPEN,
      label: LocalizationUtils.getStatus(PurchaseOrderStatus.OPEN),
    },
    {
      value: PurchaseOrderStatus.CANCELLED,
      label: LocalizationUtils.getStatus(PurchaseOrderStatus.CANCELLED),
    },
    {
      value: PurchaseOrderStatus.COMPLETED,
      label: LocalizationUtils.getStatus(PurchaseOrderStatus.COMPLETED),
    },
  ];

  readonly translations = {
    orderDate: $localize`:@@purchaseorder.orderdate:Order date`,
    deliveryDate: $localize`:@@purchaseorder.deliverydate:Delivery date`,
    supplierOrg: $localize`:@@purchaseorder.supplierorg:Supplier organisation`,
    buyerOrg: $localize`:@@purchaseorder.buyerorg:Buyer organisation`,
    deliverToLocation: $localize`:@@purchaseorder.delivertolocation:Deliver to location`,
    status: $localize`:@@purchaseorder.status:Status`,
    note: $localize`:@@purchaseorder.note:Note`,
    organisationTp: $localize`:@@purchaseorder.addneworganisation:Add new organisation`,
    locationTp: $localize`:@@purchaseorder.addnewlocation:Add new location`,
    purchaseOrderIdLabel: TextConstants.NUMBER_ID,
    suffixIconTp: $localize`:@@purchaseorder.suffix-IconTp:Generate purchase order number`,
    selectMyOrgTxt: $localize`:@@purchaseorder.selectMyOrg:Select my org `,
  };

  async onAddOrganisationClicked(formCtrlName: string) {
    const result = await this.overlay.openDialog<
      { hasSaved: boolean; savedOrganisation: any },
      { countryOptions: ISelectOption[] }
    >(AddConnectionDialogComponent, {
      countryOptions: this.countryOptions(),
    });

    if (result?.hasSaved) {
      if (formCtrlName === "buyer") {
        this.updateBuyerOrganisation.emit(result.savedOrganisation.id);
      } else {
        this.updateSupplierOrganisation.emit(result.savedOrganisation.id);
      }
    }
  }

  async onAddLocationClicked() {
    const result = await this.overlay.openDialog<
      { hasSaved: boolean; location: ILocationExtended },
      { countryOptions: ISelectOption[] }
    >(AddLocationDialogComponent, {
      countryOptions: this.countryOptions(),
    });

    if (result?.hasSaved) {
      this.updateDeliverToLocation.emit(result.location.id);
    }
  }

  getOrganisationLink(organisationId: string): NavigationParams {
    return <NavigationParams>this.router.getOrganisationLink(organisationId, false);
  }

  getLocationLink(id: string): NavigationParams {
    return this.routerService.getLocationLink(id, false) as NavigationParams;
  }

  selectOrganisation(controlName: string) {
    this.formGroup.controls[controlName].setValue(this.activeOrganisationOption());
  }

  generateItemNumber() {
    const number = CommonUtils.generateRandomString(10);

    this.formGroup.controls["purchaseOrderId"].setValue(number);
  }
}
